/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import * as z from 'zod';
/** Most tiles a split holds; a fuller row or column nests a new split instead. */
export const MAX_TILE_SPLIT_CHILDREN = 8;
/**
 * Tiled windows as a split tree: a split lays its children out in a row or column by weight. A
 * slot is free space offered to the next app opened (or dropped) there.
 */
export const desktopTileNodeSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('window'), id: z.string().min(1) }),
    z.object({ kind: z.literal('slot'), id: z.string().min(1) }),
    z.object({
        kind: z.literal('split'),
        axis: z.enum(['row', 'column']),
        weights: z.array(z.number().positive()).min(2).max(MAX_TILE_SPLIT_CHILDREN),
        // The annotation breaks the type's self-reference (Zod's recursive-schema pattern).
        get children() {
            return z.array(desktopTileNodeSchema).min(2).max(MAX_TILE_SPLIT_CHILDREN);
        },
    }),
]);
/** Every split has one weight per child (checked on the session: refining the recursive object
 * itself would make its type circular). */
const hasWeightPerTile = (node) => node.kind !== 'split' ||
    (node.weights.length === node.children.length && node.children.every(hasWeightPerTile));
/** Validates a desktop session from the client (server) or local storage (session journal). */
export const desktopSessionStateSchema = z.object({
    version: z.literal(1),
    windows: z.array(z.object({
        id: z.string().min(1),
        appId: z.string().min(1),
        title: z.string().min(1),
        path: z.string().min(1),
        history: z.array(z.string().min(1)).max(50).optional(),
        routeState: z.record(z.string(), z.unknown()).optional(),
        tabs: z
            .array(z.object({
            id: z.string().min(1),
            path: z.string().min(1),
            history: z.array(z.string().min(1)).max(50).optional(),
            historyIndex: z.number().int().nonnegative().optional(),
            routeState: z.record(z.string(), z.unknown()).optional(),
        }))
            .min(1)
            .max(20)
            .refine((tabs) => new Set(tabs.map(({ id }) => id)).size === tabs.length, 'Tab IDs must be unique')
            .optional(),
        selectedTabId: z.string().optional(),
        restoreBounds: z
            .object({
            x: z.number(),
            y: z.number(),
            width: z.number().positive(),
            height: z.number().positive(),
            isMaximized: z.boolean(),
        })
            .optional(),
        state: z.enum(['focused', 'minimized', 'open']),
        bounds: z
            .object({
            x: z.number(),
            y: z.number(),
            width: z.number().positive(),
            height: z.number().positive(),
        })
            .optional(),
        zIndex: z.number().optional(),
        isMaximized: z.boolean().optional(),
        createdAt: z.string().min(1),
        updatedAt: z.string().min(1),
    })),
    focusedWindowId: z.string().nullable().optional(),
    selectedProjectId: z.string().nullable().optional(),
    tileLayout: desktopTileNodeSchema
        .refine(hasWeightPerTile, { message: 'Each tile needs a weight' })
        .optional(),
    showWidgets: z.boolean(),
    updatedAt: z.string().min(1),
});
/** Merge patch for one view: `set` keys replace stored ones, `remove` keys are deleted. */
export const desktopWindowStatePatchSchema = z.object({
    view: z.string(),
    set: z.record(z.string(), z.unknown()).default({}),
    remove: z.array(z.string()).default([]),
});
//# sourceMappingURL=agent-desktop.js.map